import { MatterSubItemEventLogsContextMenu } from "@/components/features/matter/event-logs/MatterSubItemEventLogsContextMenu";
import { MatterSubItemEventLogsModal } from "@/components/features/matter/event-logs/MatterSubItemEventLogsModal";
import { MatterSubItemFileReferencesContextMenu } from "@/components/features/matter/file-repo/MatterSubItemFileReferencesContextMenu";
import { MatterSubItemFileReferencesModal } from "@/components/features/matter/file-repo/MatterSubItemFileReferencesModal";
import { FileReferences } from "@/components/features/matter/file-repo/FileReferences";
import {
  MatterSubItemFileReferenceUpload,
  type MatterSubItemFileReferenceUploadHandle,
} from "@/components/features/matter/file-repo/MatterSubItemFileReferenceUpload";
import { MatterSubItemNotesContextMenu } from "@/components/features/matter/notes/MatterSubItemNotesContextMenu";
import { MatterSubItemNotesModal } from "@/components/features/matter/notes/MatterSubItemNotesModal";
import { MatterSubItemTasksContextMenu } from "@/components/features/matter/tasks/MatterSubItemTasksContextMenu";
import { MatterSubItemTasksModal } from "@/components/features/matter/tasks/MatterSubItemTasksModal";
import { MatterSubItemGenerateDocumentContextMenu } from "@/components/features/matter/context-menus/MatterSubItemGenerateDocumentContextMenu";
import { MatterSubItemCreateSignatureRequestContextMenu } from "@/components/features/matter/context-menus/MatterSubItemCreateSignatureRequestContextMenu";
import {
  ActionConfirmationModal,
  ContextMenu,
  ContextMenuSeparator,
} from "@/components/ui";
import type { Matter, MatterSubItemType } from "@/lib/types";
import { useRef, useState, type ReactNode } from "react";

interface MatterSubItemContextMenuProps {
  actions?: ReactNode;
  copyValue: string;
  createSignatureRequest?: boolean;
  delete?:
    | false
    | {
        disabled?: boolean;
        onSelect: () => Promise<void>;
      };
  entityLabel: string;
  entityName: string;
  matter: Matter;
  onEdit?: () => void;
  subItem: { id: string; type: MatterSubItemType };
  trigger: ReactNode;
}

export const MatterSubItemContextMenu = ({
  actions,
  copyValue,
  createSignatureRequest = false,
  delete: deleteAction = false,
  entityLabel,
  entityName,
  matter,
  onEdit,
  subItem,
  trigger,
}: MatterSubItemContextMenuProps) => {
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isEventLogsOpen, setIsEventLogsOpen] = useState(false);
  const fileReferenceUploadRef =
    useRef<MatterSubItemFileReferenceUploadHandle>(null);
  const [isFileReferencesOpen, setIsFileReferencesOpen] = useState(false);
  const [isCreateFileReferenceOpen, setIsCreateFileReferenceOpen] =
    useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isTasksOpen, setIsTasksOpen] = useState(false);

  return (
    <>
      <ContextMenu
        entityName={entityName}
        open={onEdit ? { onSelect: onEdit } : false}
        copy={{ value: copyValue, successLabel: `${entityName} details` }}
        delete={
          deleteAction
            ? {
                disabled: deleteAction.disabled,
                onSelect: () => {
                  setIsDeleteConfirmationOpen(true);
                },
              }
            : false
        }
        trigger={trigger}
      >
        {actions}
        {actions ? <ContextMenuSeparator /> : null}
        <MatterSubItemGenerateDocumentContextMenu
          matter={matter}
          subItem={subItem}
        />
        {createSignatureRequest ? (
          <MatterSubItemCreateSignatureRequestContextMenu
            matter={matter}
            subItem={subItem}
          />
        ) : null}
        <MatterSubItemTasksContextMenu
          matter={matter}
          onOpenTasks={() => {
            setIsTasksOpen(true);
          }}
          subItem={subItem}
        />
        <MatterSubItemNotesContextMenu
          matter={matter}
          onOpenNotes={() => {
            setIsNotesOpen(true);
          }}
          subItem={subItem}
        />
        <MatterSubItemFileReferencesContextMenu
          onOpenFileReferences={() => {
            setIsFileReferencesOpen(true);
          }}
          onCreateFileReferences={() => {
            setIsCreateFileReferenceOpen(true);
          }}
          onUploadFileReference={() => {
            fileReferenceUploadRef.current?.openFilePicker();
          }}
        />
        <MatterSubItemEventLogsContextMenu
          onOpenEventLogs={() => {
            setIsEventLogsOpen(true);
          }}
        />
      </ContextMenu>
      {deleteAction ? (
        <ActionConfirmationModal
          open={isDeleteConfirmationOpen}
          setOpen={(open) => {
            if (!open && !deleteAction.disabled) {
              setIsDeleteConfirmationOpen(false);
            }
          }}
          title={`Are you sure you want to delete "${entityLabel}"?`}
          description="This action cannot be undone."
          buttonText={`Delete ${entityName}`}
          afterActionFn={deleteAction.onSelect}
          isActionPending={deleteAction.disabled}
        />
      ) : null}
      <MatterSubItemTasksModal
        open={isTasksOpen}
        onOpenChange={setIsTasksOpen}
        entityLabel={entityLabel}
        matter={matter}
        subItem={subItem}
      />
      <MatterSubItemNotesModal
        open={isNotesOpen}
        onOpenChange={setIsNotesOpen}
        entityLabel={entityLabel}
        matter={matter}
        subItem={subItem}
      />
      <MatterSubItemFileReferencesModal
        open={isFileReferencesOpen}
        onOpenChange={(open) => {
          setIsFileReferencesOpen(open);
          if (!open) {
            setIsCreateFileReferenceOpen(false);
          }
        }}
        createFileReferenceOpen={
          isFileReferencesOpen ? isCreateFileReferenceOpen : false
        }
        onCreateFileReferenceOpenChange={setIsCreateFileReferenceOpen}
        entityLabel={entityLabel}
        matter={matter}
        subItem={subItem}
      />
      <MatterSubItemEventLogsModal
        open={isEventLogsOpen}
        onOpenChange={setIsEventLogsOpen}
        entityLabel={entityLabel}
        matter={matter}
        subItem={subItem}
      />
      {isCreateFileReferenceOpen && !isFileReferencesOpen ? (
        <FileReferences
          matter={matter}
          subItem={subItem}
          createOnly
          createFileReferenceOpen
          onCreateFileReferenceOpenChange={setIsCreateFileReferenceOpen}
        />
      ) : null}
      <MatterSubItemFileReferenceUpload
        ref={fileReferenceUploadRef}
        matter={matter}
        subItem={subItem}
      />
    </>
  );
};
